import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  usePropertiesAPI,
  UsePropertiesAPIReturn,
} from 'src/SharedCustomHooks/UsePropertiesAPI/usePropertiesAPI';
import {
  buildPropertyCreateParams,
  buildPropertyUpdateParams,
} from 'src/SharedCustomHooks/UsePropertiesAPI/helpers';
import { Property } from 'src/SharedCustomHooks/UseSuiteActions/types';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectItems } from 'src/store/selectors/items';
import { upload } from 'src/helpers/upLoadHelper';
import { setSearch, clearSearch } from 'src/store/actions/search';
import { clearServiceRequests } from 'src/store/actions/serviceRequests';
import { fetchSuiteAreas, clearSuites } from 'src/store/actions/suites';
import { clearFaqsNotesDocuments } from 'src/store/actions/faqsNotesDocuments';
import { fetchItems } from 'src/store/actions/items';
import {
  fetchtTabList,
  clearSelectedModulePermission,
} from 'src/store/actions/tabSelections';
import { stopLoaders } from 'src/store/actions/loader';
import { clearActiveTab } from 'src/store/actions/activeTab';

interface SavePropertyResult {
  ok: boolean;
}

interface DeletePropertyResult {
  ok: boolean;
}

interface ImageUploadResult {
  ok: boolean;
  imageUrl: string | null;
}

export interface UsePropertiesPageOrchestratorReturn {
  properties: Property[];
  isLoadingProperties: boolean;
  tabList: any[];
  totalSuites: number;
  totalServiceRequests: number;
  saveProperty: (formData: any) => Promise<SavePropertyResult>;
  deleteProperty: (property: any) => Promise<DeletePropertyResult>;
  searchProperties: (searchTerm: string) => void;
  applyWalkthroughSyncState: (propertyId: string, isSuspended: boolean) => void;
  clearModulePermissionSelection: () => void;
}

export const usePropertiesPageOrchestrator =
  (): UsePropertiesPageOrchestratorReturn => {
    const dispatch = useDispatch();

    const user: any = useSelector(selectAuthenticatedUser);
    const items: any[] = useSelector(selectItems);

    // TODO: phase N — tabList still lives on the legacy tabSelections slice;
    // PropertyForm and other pages read it, so it migrates with its owner.
    const tabSelections: any = useSelector((state: any) => state.tabSelections);
    const tabList: any[] = tabSelections.tabList;

    const propertiesAPI: UsePropertiesAPIReturn = usePropertiesAPI();

    const { properties } = propertiesAPI;

    /* The hook's loading flag starts false until the mount fetch kicks off, so
     * without this guard the empty-state splash would flash on first paint. */
    const [hasCompletedInitialLoad, setHasCompletedInitialLoad] =
      useState<boolean>(false);

    const isLoadingProperties: boolean =
      propertiesAPI.isLoadingProperties || !hasCompletedInitialLoad;

    const userRoleName: string = user.roles[0].role.name;

    const refetchProperties = async (): Promise<void> => {
      await propertiesAPI.fetchProperties(user.id, userRoleName);
    };

    // TODO: phase N — cross-slice clears that keep legacy sibling pages
    // consistent on navigation; each migrates when its owning page does.
    const resetCrossPageLegacyState = (): void => {
      dispatch(stopLoaders());
      dispatch(clearServiceRequests());
      dispatch(clearSelectedModulePermission());
      dispatch(clearSearch());
      dispatch(clearSuites());
      dispatch(clearFaqsNotesDocuments());
      dispatch(clearActiveTab());
    };

    const loadPageReferenceData = (): void => {
      dispatch(fetchtTabList());
      dispatch(fetchSuiteAreas() as any);

      if (items.length === 0) {
        dispatch(fetchItems() as any);
      }
    };

    const loadPropertiesOnMount = async (): Promise<void> => {
      await refetchProperties();

      setHasCompletedInitialLoad(true);
    };

    useEffect(() => {
      resetCrossPageLegacyState();
      loadPageReferenceData();
      loadPropertiesOnMount();
    }, []);

    const uploadSelectedImage = async (
      formData: any
    ): Promise<ImageUploadResult> => {
      const selectedImage: File | undefined = formData.selectedImage?.[0];

      if (!selectedImage) {
        return { ok: true, imageUrl: null };
      }

      try {
        const imageUrl: string = await upload(selectedImage);

        return { ok: true, imageUrl };
      } catch (err) {
        console.error(
          'usePropertiesPageOrchestrator uploadSelectedImage failed:',
          err
        );

        toast.error('🚫 Property image upload failed, please try again.');

        return { ok: false, imageUrl: null };
      }
    };

    const persistProperty = async (
      formData: any,
      imageUrl: string | null
    ): Promise<{ ok: boolean }> => {
      const isExistingProperty: boolean = !!formData.propertyId;

      if (isExistingProperty) {
        const updateParams: any = buildPropertyUpdateParams(
          formData,
          user.id,
          imageUrl
        );

        return propertiesAPI.updateProperty(updateParams);
      }

      const createParams: any = buildPropertyCreateParams(
        formData,
        user.id,
        imageUrl
      );

      return propertiesAPI.createProperty(createParams);
    };

    const saveProperty = async (formData: any): Promise<SavePropertyResult> => {
      const { ok: uploadOk, imageUrl } = await uploadSelectedImage(formData);

      if (!uploadOk) {
        return { ok: false };
      }

      const { ok: persisted } = await persistProperty(formData, imageUrl);

      if (!persisted) {
        return { ok: false };
      }

      await refetchProperties();

      return { ok: true };
    };

    const deleteProperty = async (
      property: any
    ): Promise<DeletePropertyResult> => {
      if (!property?.id) {
        console.warn('deleteProperty called with no property id');

        return { ok: false };
      }

      const { ok } = await propertiesAPI.deleteProperty(property.id, user.id);

      if (!ok) {
        return { ok };
      }

      await refetchProperties();

      return { ok: true };
    };

    // TODO: phase N — search is a shared redux bus PropertiesTable reads via
    // selectSearch; decouple when the table owns its own filter state.
    const searchProperties = (searchTerm: string): void => {
      dispatch(setSearch({ properties: searchTerm }));
    };

    const applyWalkthroughSyncState = (
      propertyId: string,
      isSuspended: boolean
    ): void => {
      propertiesAPI.patchPropertyInList(propertyId, {
        syncAutomationSuspended: isSuspended,
      });
    };

    const clearModulePermissionSelection = (): void => {
      dispatch(clearSelectedModulePermission());
    };

    const totalSuites: number = properties.reduce(
      (sum: number, property: Property) => sum + (property.suits ?? 0),
      0
    );

    const totalServiceRequests: number = properties.reduce(
      (sum: number, property: Property) => sum + (property.serviceRequests ?? 0),
      0
    );

    return {
      properties,
      isLoadingProperties,
      tabList,
      totalSuites,
      totalServiceRequests,
      saveProperty,
      deleteProperty,
      searchProperties,
      applyWalkthroughSyncState,
      clearModulePermissionSelection,
    };
  };